/**
 * NoahsArkReel — Auto-play "Basketball of Chess · Noah's Ark Trap" showcase.
 *
 * Designed for screen recording (QuickTime / OBS) at 1080×1920 or 1080×1080.
 * Plays a fully scripted 7-ply sequence:
 *   1... c5   (Black kicks the queen)
 *   2.  Qd5   (White retreats)
 *   2... Be6! (Black posts the bishop)
 *   3.  Qc6+  (White check)
 *   3... Bd7  (Black blocks)
 *   4.  Qd5   (White retreats again)
 *   4... c4!  (Black closes the cage — trap sprung)
 *
 * Basketball parallel animates in sync as the 3-defender baseline cage forms.
 * Includes: 3-2-1 countdown → move-by-move captions → "Cage Complete" hero card.
 */
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Chess } from 'chess.js';
import { Chessboard } from 'react-chessboard';
import { RotateCcw, Play, Trophy, Zap, Sparkles, Download, Share2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';
import BasketballParallel from '../components/electives/BasketballParallel';

const START_FEN = 'r1bqkbnr/2pp1ppp/p1n5/1p6/3QP3/1B6/PPP2PPP/RNB1K2R b KQkq - 0 8';

// Full scripted line — both sides — 7 half-moves total.
const SCRIPT = [
  { san: 'c5',   side: 'Black', caption: 'Kick the queen.',         beat: 'The trap begins — pawn punches the queen off d4.',       progressTotal: 4, progressAt: 1 },
  { san: 'Qd5',  side: 'White', caption: 'Queen retreats.',         beat: 'Defender commits — she has to move somewhere.',           progressTotal: 4, progressAt: 1 },
  { san: 'Be6',  side: 'Black', caption: 'Post the bishop.',        beat: 'Black assigns a second defender — the wall builds.',      progressTotal: 4, progressAt: 2 },
  { san: 'Qc6+', side: 'White', caption: 'Queen checks.',           beat: 'She tries to escape with a threat — but it forces a block.', progressTotal: 4, progressAt: 2 },
  { san: 'Bd7',  side: 'Black', caption: 'Block the check.',        beat: 'Bd7 covers the check AND clears the path for c4.',        progressTotal: 4, progressAt: 3 },
  { san: 'Qd5',  side: 'White', caption: 'Queen shuffles back.',    beat: 'One last retreat — she is out of squares.',                progressTotal: 4, progressAt: 3 },
  { san: 'c4!',  side: 'Black', caption: 'Close the cage.',         beat: "Noah's Ark: a6, b5, c4 — the bishop on b3 is trapped.",   progressTotal: 4, progressAt: 4 },
];

const MOVE_INTERVAL_MS = 2600; // ~2.6s per move × 7 moves ≈ 18s + intro + outro ≈ 28-30s

const NoahsArkReel = () => {
  const [engine] = useState(() => new Chess(START_FEN));
  const [fen, setFen] = useState(engine.fen());
  const [step, setStep] = useState(-1);       // -1 = pre-roll, 0..6 = moves, 7 = outro
  const [countdown, setCountdown] = useState(3); // 3,2,1 → then play
  const [playing, setPlaying] = useState(true);
  const timerRef = useRef(null);

  // Sora 2 promo state
  const [soraStatus, setSoraStatus] = useState('none'); // none | queued | generating | ready | failed
  const [soraVideoUrl, setSoraVideoUrl] = useState(null);
  const [soraJobId, setSoraJobId] = useState(null);
  const [soraError, setSoraError] = useState(null);
  const [soraBusy, setSoraBusy] = useState(false);
  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('academy_token') || ''}` });
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const refreshSora = useCallback(async () => {
    try {
      const r = await api.get('/reel/sora/status');
      setSoraStatus(r.data.status || 'none');
      setSoraJobId(r.data.job_id || null);
      setSoraError(r.data.error || null);
      setSoraVideoUrl(r.data.video_url ? `${backendUrl}${r.data.video_url}?t=${Date.now()}` : null);
    } catch (e) {
      // silent
    }
  }, [backendUrl]);

  useEffect(() => { refreshSora(); }, [refreshSora]);

  // Poll while generating
  useEffect(() => {
    if (soraStatus !== 'queued' && soraStatus !== 'generating') return;
    const id = setInterval(refreshSora, 6000);
    return () => clearInterval(id);
  }, [soraStatus, refreshSora]);

  const triggerSora = async (force = false) => {
    setSoraBusy(true);
    setSoraError(null);
    try {
      const r = await api.post('/reel/sora/generate', { force }, { headers: authHeaders() });
      if (r.data?.status === 'already_ready') {
        toast.info('Promo already generated — pass "Force Regenerate" to rebuild it.');
      } else {
        toast.success('Sora 2 promo queued — this takes 2 to 5 minutes. You can leave this page and come back.');
      }
      await refreshSora();
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || 'Failed to start generation';
      toast.error(detail);
      setSoraError(detail);
    } finally {
      setSoraBusy(false);
    }
  };

  const shareSora = async () => {
    if (!soraVideoUrl) return;
    try {
      if (navigator.share) {
        await navigator.share({
          title: 'The Basketball of Chess — Noah\u2019s Ark Trap',
          text: 'On the court. On the board. Same trap.',
          url: soraVideoUrl,
        });
      } else {
        await navigator.clipboard.writeText(soraVideoUrl);
        toast.success('Video URL copied to clipboard.');
      }
    } catch (e) { /* user canceled */ }
  };

  const reset = useCallback(() => {
    clearTimeout(timerRef.current);
    engine.load(START_FEN);
    setFen(engine.fen());
    setStep(-1);
    setCountdown(3);
    setPlaying(true);
  }, [engine]);

  // Countdown 3 → 2 → 1 → play first move
  useEffect(() => {
    if (!playing) return;
    if (step !== -1) return;
    if (countdown === 0) {
      setStep(0);
      return;
    }
    timerRef.current = setTimeout(() => setCountdown((c) => c - 1), 900);
    return () => clearTimeout(timerRef.current);
  }, [countdown, step, playing]);

  // Play scripted moves
  useEffect(() => {
    if (!playing) return;
    if (step < 0 || step >= SCRIPT.length) return;
    const move = SCRIPT[step];
    // Apply the move on the engine
    try {
      const clean = move.san.replace(/[!?+#]/g, '');
      engine.move(clean);
      setFen(engine.fen());
    } catch (e) {
      // ignore
    }
    timerRef.current = setTimeout(() => setStep((s) => s + 1), MOVE_INTERVAL_MS);
    return () => clearTimeout(timerRef.current);
  }, [step, playing, engine]);

  const currentBeat = useMemo(() => {
    if (step === -1) return null;
    if (step >= SCRIPT.length) return null;
    return SCRIPT[step];
  }, [step]);

  const cageComplete = step >= SCRIPT.length;
  // progress for basketball parallel — driven by which black move has been played
  const progress = cageComplete ? 4 : (currentBeat ? currentBeat.progressAt : 0);

  return (
    <div
      className="min-h-screen w-full flex items-center justify-center px-4 py-6"
      style={{
        background: 'radial-gradient(1200px 800px at 20% 10%, #1A1509 0%, #05050A 60%)',
        color: '#F5ECC8',
      }}
      data-testid="reel-noahs-ark"
    >
      <div className="w-full max-w-6xl">
        {/* Hero header — always visible so screen-capture has consistent branding */}
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div>
            <div className="text-[10px] font-black tracking-[0.32em] uppercase" style={{ color: '#C9A961' }}>
              Scholar Athlete Academy · Elective
            </div>
            <div className="text-2xl md:text-4xl font-black uppercase tracking-tight leading-none mt-1"
                 style={{ color: '#F5ECC8', letterSpacing: '-0.02em' }}>
              The Basketball of Chess
            </div>
            <div className="text-sm md:text-base font-black tracking-[0.18em] uppercase mt-1"
                 style={{ color: '#FF5E00' }}>
              Week 4 · Noah&apos;s Ark Trap
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={reset}
              className="inline-flex items-center gap-1.5 bg-zinc-900/80 hover:bg-zinc-800 border border-zinc-700 text-white px-3 py-2 text-[10px] font-black uppercase tracking-widest"
              data-testid="reel-restart"
            >
              <RotateCcw className="w-3 h-3" /> Restart
            </button>
            <button
              onClick={() => setPlaying((p) => !p)}
              className="inline-flex items-center gap-1.5 bg-amber-500 hover:bg-amber-400 text-black px-3 py-2 text-[10px] font-black uppercase tracking-widest"
              data-testid="reel-play-toggle"
            >
              <Play className="w-3 h-3" /> {playing ? 'Pause' : 'Play'}
            </button>
          </div>
        </div>

        {/* Main stage: chess left, basketball right */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 relative">
          {/* Countdown overlay */}
          {step === -1 && countdown > 0 && (
            <div className="absolute inset-0 z-30 flex items-center justify-center bg-black/70 backdrop-blur-sm" data-testid="reel-countdown">
              <div className="text-center">
                <div className="text-[9px] font-black tracking-[0.4em] uppercase" style={{ color: '#C9A961' }}>
                  Rolling
                </div>
                <div className="text-[128px] md:text-[180px] leading-none font-black" style={{ color: '#FF5E00' }}>
                  {countdown}
                </div>
              </div>
            </div>
          )}

          {/* Outro overlay */}
          {cageComplete && (
            <div className="absolute inset-0 z-30 flex items-center justify-center bg-black/85 backdrop-blur-sm" data-testid="reel-outro">
              <div className="text-center px-6">
                <Trophy className="w-14 h-14 mx-auto mb-3" style={{ color: '#C9A961' }} />
                <div className="text-[10px] font-black tracking-[0.4em] uppercase" style={{ color: '#C9A961' }}>
                  Cage Complete
                </div>
                <div className="text-3xl md:text-5xl font-black mt-1" style={{ color: '#F5ECC8', letterSpacing: '-0.02em' }}>
                  The bishop is trapped.
                </div>
                <div className="text-sm md:text-base mt-3 text-zinc-300 font-bold">
                  a6 · b5 · c4 — the pawn cage forms the exact geometry of a baseline 2-on-1 trap.
                </div>
                <div className="mt-5 inline-flex items-center gap-2 border border-amber-500/60 bg-amber-500/10 px-4 py-2">
                  <Zap className="w-4 h-4" style={{ color: '#C9A961' }} />
                  <span className="text-[10px] md:text-xs font-black tracking-[0.28em] uppercase" style={{ color: '#C9A961' }}>
                    +25 Scholar Dollars · Week 4 Double Trap Bonus
                  </span>
                </div>
                <div className="mt-6 text-[10px] md:text-xs font-black tracking-[0.32em] uppercase" style={{ color: '#F5ECC8' }}>
                  scholarathlete.academy
                </div>
                <div className="text-[10px] font-bold tracking-[0.24em] uppercase mt-1 text-zinc-500">
                  6-Week Elective · Grades 4–9
                </div>
                <button
                  onClick={reset}
                  className="mt-6 inline-flex items-center gap-1.5 bg-amber-500 hover:bg-amber-400 text-black px-4 py-2 text-[10px] font-black uppercase tracking-widest"
                  data-testid="reel-replay"
                >
                  <RotateCcw className="w-3 h-3" /> Play Again
                </button>
              </div>
            </div>
          )}

          {/* Left: chess board */}
          <div className="border-2 p-4 bg-black/40" style={{ borderColor: '#EF4444' }}>
            <div className="flex items-center gap-2 flex-wrap mb-2">
              <span className="text-[9px] font-black tracking-[0.28em] uppercase px-2 py-0.5"
                    style={{ backgroundColor: '#EF4444', color: '#fff' }}>
                The Board
              </span>
              <span className="text-[9px] font-black tracking-widest uppercase text-zinc-500">
                Ruy Lopez · Position after 8.Qxd4??
              </span>
            </div>
            <div style={{ maxWidth: 460, margin: '0 auto' }}>
              <Chessboard
                position={fen}
                boardOrientation="black"
                arePiecesDraggable={false}
                customDarkSquareStyle={{ backgroundColor: '#8B6C42' }}
                customLightSquareStyle={{ backgroundColor: '#F5ECC8' }}
                customBoardStyle={{ borderRadius: '0', boxShadow: '0 0 0 1px #C9A96140' }}
              />
            </div>
            {/* Move ledger */}
            <div className="mt-3">
              <div className="text-[10px] font-black uppercase tracking-widest text-zinc-500 mb-1">Move Ledger</div>
              <div className="text-[11px] font-mono">
                {SCRIPT.map((m, i) => (
                  <span
                    key={i}
                    className={`inline-block mr-2 ${i <= step ? '' : 'opacity-30'}`}
                    style={{ color: i <= step ? (m.side === 'Black' ? '#C9A961' : '#F5ECC8') : '#4B5563' }}
                  >
                    {m.side === 'Black' ? '' : ''}{m.san}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Right: basketball parallel */}
          <div className="border-2 p-4 bg-black/40" style={{ borderColor: '#3B82F6' }}>
            <div className="flex items-center gap-2 flex-wrap mb-2">
              <span className="text-[9px] font-black tracking-[0.28em] uppercase px-2 py-0.5"
                    style={{ backgroundColor: '#3B82F6', color: '#fff' }}>
                The Court Parallel
              </span>
              <span className="text-[9px] font-black tracking-widest uppercase text-zinc-500">
                Baseline 2-on-1 Trap
              </span>
            </div>
            <BasketballParallel week={4} module="B" progress={progress} total={4} kind="set" />
          </div>
        </div>

        {/* Caption bar — the "reel narration" that syncs to the current move */}
        <div className="mt-4 border-t-2 border-b-2 py-4 px-5 flex items-center gap-4 flex-wrap"
             style={{ borderColor: '#C9A961', backgroundColor: '#0A0A0E' }}>
          {currentBeat ? (
            <>
              <div className="text-[9px] font-black tracking-[0.32em] uppercase" style={{ color: '#C9A961' }}>
                {currentBeat.side} plays
              </div>
              <div className="text-2xl md:text-4xl font-black" style={{ color: '#F5ECC8', letterSpacing: '-0.01em' }}>
                {currentBeat.san}
              </div>
              <div className="text-[10px] md:text-xs font-black tracking-[0.24em] uppercase" style={{ color: '#FF5E00' }}>
                {currentBeat.caption}
              </div>
              <div className="text-xs md:text-sm text-zinc-300 flex-1 min-w-[220px]">
                {currentBeat.beat}
              </div>
            </>
          ) : cageComplete ? (
            <div className="text-xs md:text-sm text-zinc-400 font-bold">
              Sequence complete — restart to replay for capture.
            </div>
          ) : (
            <div className="text-xs md:text-sm text-zinc-400 font-bold">
              Reel starting…
            </div>
          )}
        </div>

        {/* Footer strap — persistent branding for the recording */}
        <div className="mt-4 flex items-center justify-between flex-wrap gap-2 text-[10px] font-black tracking-[0.28em] uppercase" style={{ color: '#C9A961' }}>
          <div>scholarathlete.academy</div>
          <div className="text-zinc-500">6-Week Elective · Chess + Basketball · Grades 4–9</div>
          <div style={{ color: '#FF5E00' }}>Free Preview at /academy/electives</div>
        </div>

        {/* Sora 2 Promo Cut — shareable video */}
        <div className="mt-8 border-2 p-5" style={{ borderColor: '#C9A961', background: '#0A0A0E' }} data-testid="sora-promo-section">
          <div className="flex items-center gap-2 flex-wrap mb-3">
            <Sparkles className="w-4 h-4" style={{ color: '#C9A961' }} />
            <span className="text-[10px] font-black tracking-[0.32em] uppercase" style={{ color: '#C9A961' }}>
              Sora 2 Promo Cut · 12s · Vertical
            </span>
            <span className="ml-auto text-[9px] font-black tracking-[0.28em] uppercase" style={{
              color: soraStatus === 'ready' ? '#10B981' : soraStatus === 'failed' ? '#EF4444' : '#F59E0B',
            }} data-testid="sora-status-pill">
              {soraStatus === 'ready' ? 'Ready' :
               soraStatus === 'generating' ? 'Generating (2–5 min)' :
               soraStatus === 'queued' ? 'Queued' :
               soraStatus === 'failed' ? 'Failed' : 'Not Generated'}
            </span>
          </div>

          {soraStatus === 'ready' && soraVideoUrl ? (
            <div className="grid md:grid-cols-[280px_1fr] gap-5 items-start">
              <video
                key={soraVideoUrl}
                src={soraVideoUrl}
                controls
                playsInline
                className="w-full max-w-[280px] border-2 border-zinc-800 bg-black"
                data-testid="sora-video-player"
              />
              <div>
                <div className="text-lg md:text-xl font-black" style={{ color: '#F5ECC8', letterSpacing: '-0.01em' }}>
                  Share this instead of screen-recording.
                </div>
                <p className="text-xs md:text-sm text-zinc-400 mt-2 leading-relaxed">
                  A 12-second, portrait-format branded promo generated by Sora 2 — synced split-screen of the Noah&apos;s Ark chess trap and the baseline basketball cage, with a scripted narrator voiceover. Send the link, download the MP4, or drop it into Instagram / TikTok / a text message.
                </p>
                <div className="mt-3 flex items-center gap-2 flex-wrap">
                  <a
                    href={soraVideoUrl}
                    download="basketball-of-chess-noahs-ark.mp4"
                    className="inline-flex items-center gap-1.5 bg-amber-500 hover:bg-amber-400 text-black px-4 py-2 text-[10px] font-black uppercase tracking-widest"
                    data-testid="sora-download"
                  >
                    <Download className="w-3 h-3" /> Download MP4
                  </a>
                  <button
                    onClick={shareSora}
                    className="inline-flex items-center gap-1.5 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-white px-4 py-2 text-[10px] font-black uppercase tracking-widest"
                    data-testid="sora-share"
                  >
                    <Share2 className="w-3 h-3" /> Share Link
                  </button>
                  <button
                    onClick={() => triggerSora(true)}
                    disabled={soraBusy}
                    className="inline-flex items-center gap-1.5 border border-zinc-700 hover:border-amber-500 text-zinc-400 hover:text-amber-400 px-4 py-2 text-[10px] font-black uppercase tracking-widest disabled:opacity-40"
                    data-testid="sora-regenerate"
                  >
                    {soraBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />} Force Regenerate
                  </button>
                </div>
              </div>
            </div>
          ) : soraStatus === 'generating' || soraStatus === 'queued' ? (
            <div className="flex items-center gap-3 py-4">
              <Loader2 className="w-5 h-5 animate-spin" style={{ color: '#C9A961' }} />
              <div>
                <div className="text-sm font-black" style={{ color: '#F5ECC8' }}>
                  Sora 2 is rendering your promo…
                </div>
                <div className="text-[11px] text-zinc-400 mt-1">
                  Typically takes 2 to 5 minutes. You can leave this tab — the video will appear here when it&apos;s ready. Status auto-refreshes every 6 seconds.
                </div>
                {soraJobId && <div className="text-[9px] font-mono text-zinc-600 mt-1">Job {soraJobId.slice(0, 8)}…</div>}
              </div>
            </div>
          ) : (
            <div>
              <p className="text-xs md:text-sm text-zinc-400 leading-relaxed mb-3">
                Generate a shareable 12-second vertical promo of this exact scene (chess + basketball parallel + narrator voiceover) so parents can share the elective without screen-recording anything. Admin / teacher only — one canonical clip serves every visitor.
              </p>
              {soraError && (
                <div className="text-[11px] text-red-400 font-bold mb-3 border border-red-500/40 bg-red-500/10 px-3 py-2" data-testid="sora-error">
                  {soraError}
                </div>
              )}
              <button
                onClick={() => triggerSora(false)}
                disabled={soraBusy}
                className="inline-flex items-center gap-1.5 bg-amber-500 hover:bg-amber-400 disabled:opacity-40 text-black px-5 py-2.5 text-[10px] font-black uppercase tracking-widest"
                data-testid="sora-generate"
              >
                {soraBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} Generate Sora 2 Promo
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NoahsArkReel;
